import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  AIWorkflow,
  AR_AGING_BASES,
  AR_RECOMMENDATION_SPEED_PREFERENCES,
  EMAIL_PROVIDER,
  ModuleConfig,
  ProductModule,
} from "@erp/shared-types";

// Validation schemas
const productModuleSchema = z.enum(ProductModule);

const moduleConfigSchema: z.ZodType<ModuleConfig> = z
  .object({
    emailWorkflows: z
      .partialRecord(
        z.enum(AIWorkflow),
        z.object({
          registeredEmailDomains: z.array(
            z.object({
              header: z.enum(["to", "cc"]),
              domain: z.string(),
            }),
          ),
        }),
      )
      .optional(),
    rebates: z
      .object({
        reportedAlgorithm: z
          .enum(["linear-regression", "conservative"])
          .optional(),
        conservativeRiskFactor: z.number().min(0).max(1).optional(),
        seasonalDampeningFactor: z.number().min(0).max(1).optional(),
        enabledStrategies: z
          .array(z.enum(["linear-regression", "conservative"]))
          .optional(),
        perYear: z
          .record(
            z.string().regex(/^\d{4}$/),
            z.object({
              reportedAlgorithm: z
                .enum(["linear-regression", "conservative"])
                .optional(),
              conservativeRiskFactor: z.number().min(0).max(1).optional(),
              seasonalDampeningFactor: z.number().min(0).max(1).optional(),
              enabledStrategies: z
                .array(z.enum(["linear-regression", "conservative"]))
                .optional(),
            }),
          )
          .optional(),
        followupMailboxUpn: z.string().optional(),
        followupTemplateId: z.string().uuid().optional(),
        followupContactTagOptionId: z.string().uuid().optional(),
        followupCadenceDays: z.number().optional(),
      })
      .optional(),
    ap: z
      .object({
        invoiceLineItemPurchaseMatching: z
          .array(z.array(z.string()))
          .optional(),
        disableRemittanceEmail: z.boolean().optional(),
        remittanceMailboxUpn: z.string().email().optional(),
        remittanceSenderAddress: z.string().email().optional(),
        remittanceContactEmails: z.array(z.string().email()).optional(),
        remittanceContactPhone: z.string().optional(),
        paymentScheduling: z
          .object({
            mode: z.enum(["immediate", "payRun", "latestDate"]),
            cron: z.string().optional(),
            immediacy: z.enum(["asap", "latest"]).optional(),
            rounding: z.enum(["up", "down", "nearest"]).optional(),
            timezone: z.string().optional(),
            daysBeforeDue: z.number().positive().optional(),
            paymentTermsPriority: z
              .array(z.enum(["invoice", "supplier", "company"]))
              .refine(
                (sources) => new Set(sources).size === sources.length,
                "paymentTermsPriority must not contain duplicate sources",
              )
              .optional(),
          })
          .optional(),
        defaultOutgoingPaymentTime: z
          .string()
          .regex(
            /^([01]\d|2[0-3]):[0-5]\d$/,
            "Must be in HH:mm 24-hour format (e.g. 09:00, 14:30)",
          )
          .optional(),
        approvalThresholds: z
          .array(
            z.object({
              minAmount: z.number(),
              requiredApprovals: z.number(),
            }),
          )
          .optional(),
      })
      .refine(
        (value) =>
          !value.remittanceSenderAddress || !!value.remittanceMailboxUpn,
        {
          message: "remittanceSenderAddress requires remittanceMailboxUpn",
          path: ["remittanceSenderAddress"],
        },
      )
      .optional(),
    ar: z
      .object({
        outboxMailboxUpn: z.string().email().optional(),
        outboxSenderAddress: z.string().email().optional(),
        outboxContactEmails: z.array(z.string().email()).optional(),
        outboxContactPhone: z.string().optional(),
        logoUrl: z.string().url().optional(),
        defaultPdfFilenamePrefix: z.string().max(64).optional(),
        internalOnlyEnabled: z.boolean().optional(),
        internalOnlyDomains: z.array(z.string().min(1)).optional(),
        statementDefaultBasis: z.enum(AR_AGING_BASES).optional(),
        lockStatementBasis: z.boolean().optional(),
        collections: z
          .object({
            autoDetectShortPaysEnabled: z.boolean().optional(),
            shortPayMinimumAmount: z
              .string()
              .regex(/^\d+(\.\d+)?$/, {
                message: "Must be a non-negative decimal string",
              })
              .optional(),
            shortPayGraceDays: z.number().int().min(0).optional(),
            autoDetectOverPaymentsEnabled: z.boolean().optional(),
            overPaymentMinimumAmount: z
              .string()
              .regex(/^\d+(\.\d+)?$/, {
                message: "Must be a non-negative decimal string",
              })
              .optional(),
            autoDetectDeductionsEnabled: z.boolean().optional(),
            deductionMinimumAmount: z
              .string()
              .regex(/^\d+(\.\d+)?$/, {
                message: "Must be a non-negative decimal string",
              })
              .optional(),
            defaultAssigneeUserId: z.string().uuid().optional(),
            recommendations: z
              .object({
                speedPreference: z
                  .enum(AR_RECOMMENDATION_SPEED_PREFERENCES)
                  .optional(),
              })
              .optional(),
          })
          .optional(),
      })
      // Preserve unknown keys under `ar` (e.g. the AR Vision Demo's `ar.demo`
      // block) instead of silently stripping them on save — the top-level
      // module config is already passthrough; this nested object was not.
      .passthrough()
      .refine(
        (value) => !value.outboxSenderAddress || !!value.outboxMailboxUpn,
        {
          message: "outboxSenderAddress requires outboxMailboxUpn",
          path: ["outboxSenderAddress"],
        },
      )
      .refine(
        (value) =>
          !value.internalOnlyEnabled ||
          (value.internalOnlyDomains?.length ?? 0) > 0,
        {
          message: "internalOnlyEnabled requires at least one internal domain",
          path: ["internalOnlyDomains"],
        },
      )
      .refine(
        (value) => !value.lockStatementBasis || value.statementDefaultBasis,
        {
          message: "lockStatementBasis requires statementDefaultBasis",
          path: ["statementDefaultBasis"],
        },
      )
      .optional(),
    orderManagement: z
      .object({
        enabledCustomerIds: z.array(z.string()).optional(),
        disabledCustomerIds: z.array(z.string()).optional(),
        requestDateStrategy: z
          .enum(["inventory-lead-time", "shipping-method"])
          .optional(),
        autoSubmitEnabled: z.boolean().optional(),
        manualApprovalEnabled: z.boolean().optional(),
        skipDescriptionBasedProductMatching: z.boolean().optional(),
        legacyDraftAssignmentEmailsDisabled: z.boolean().optional(),
        mergeSplitPurchaseOrdersEnabled: z.boolean().optional(),
        enhancedSalesOrderUi: z.boolean().optional(),
        defaultPriceListId: z.string().uuid().optional(),
        costCurrency: z.enum(["USD", "EUR"]).optional(),
      })
      .optional(),
    shipAndDebit: z
      .object({
        sd_844_email_provider: z
          .enum([EMAIL_PROVIDER.SENDGRID, EMAIL_PROVIDER.MICROSOFT_GRAPH])
          .optional(),
        sd_844_email_mailbox_upn: z.string().optional(),
      })
      .refine(
        (val) =>
          val.sd_844_email_provider !== EMAIL_PROVIDER.MICROSOFT_GRAPH ||
          !!val.sd_844_email_mailbox_upn,
        "sd_844_email_mailbox_upn is required when sd_844_email_provider is microsoft_graph",
      )
      .optional(),
    procurement: z
      .object({
        voiceAgentConfigId: z.string().uuid().optional(),
        phoneNumberToCall: z.string().min(3).optional(),
      })
      .optional(),
    oracleEbs: z
      .object({
        queryServerUrl: z.string().optional(),
        defaultOrgCode: z.string().optional(),
        defaultOrderType: z
          .object({ id: z.number(), name: z.string() })
          .optional(),
        defaultPriceListName: z.string().optional(),
        defaultInventoryOrgId: z.number().optional(),
        ebsOrgName: z.string().optional(),
      })
      .optional(),
    mappings: z
      .record(z.string(), z.record(z.string(), z.unknown()))
      .optional(),
    mappingsMeta: z
      .record(
        z.string(),
        z.object({
          displayName: z.string().optional(),
          keyLabel: z.string().nullable().optional(),
          columnOrder: z.array(z.string()).optional(),
        }),
      )
      .optional(),
  })
  .passthrough();

const organizationModuleConfigSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  module: productModuleSchema,
  enabled: z.boolean(),
  config: moduleConfigSchema,
  createdAt: z.date(),
  updatedAt: z.date(),
});

/**
 * Input uses moduleConfigSchema with .passthrough() so known fields are validated
 * (e.g., shipAndDebit.sd_844_email_provider must be "sendgrid" | "microsoft_graph")
 * while unrecognized keys are preserved. Consumers should read fields defensively
 * (e.g., config?.shipAndDebit?.sd_844_email_provider) and apply defaults at read time.
 */
const createOrUpdateOrganizationModuleConfigInputSchema = z.object({
  module: productModuleSchema,
  enabled: z.boolean().optional(),
  config: moduleConfigSchema,
});

const getModuleConfigsContract = oc
  .output(z.array(organizationModuleConfigSchema))
  .route({
    method: "GET",
    summary: "Get all module configurations for the organization",
  });

const getModuleConfigContract = oc
  .input(z.object({ module: productModuleSchema }))
  .output(organizationModuleConfigSchema)
  .route({
    method: "GET",
    summary: "Get a specific module configuration",
  });

const createOrUpdateModuleConfigContract = oc
  .input(createOrUpdateOrganizationModuleConfigInputSchema)
  .output(organizationModuleConfigSchema)
  .route({
    summary: "Update an existing module configuration",
  });

export const orgConfigModulesContract = {
  getModuleConfigs: getModuleConfigsContract,
  getModuleConfig: getModuleConfigContract,
  createOrUpdateModuleConfig: createOrUpdateModuleConfigContract,
};

export type OrgConfigModulesContract = typeof orgConfigModulesContract;
